/**
 * A piece put back with Undo is a new save to the backend — first on the account's list, last on a
 * guest's — so left alone it would come back a screen away from where it was taken off. `placedAfter`
 * maps each such piece's id to the id it followed (`null`: it was first); this moves every one of
 * them back there, or to the front when the one it followed is gone too. Pieces not in `items` are
 * skipped, and the rest keep their order.
 */
export function restorePlaces<T>(
  items: readonly T[],
  idOf: (item: T) => string,
  placedAfter: ReadonlyMap<string, string | null>,
): T[] {
  const placed = [...items]
  for (const [id, after] of placedAfter) {
    const from = placed.findIndex(item => idOf(item) === id)
    if (from < 0)
      continue
    const [item] = placed.splice(from, 1)
    placed.splice(after === null ? 0 : placed.findIndex(candidate => idOf(candidate) === after) + 1, 0, item!)
  }
  return placed
}

/** The id `id` follows in `items`, as `restorePlaces` records it: `null` for the first, `undefined` when absent. */
export function placeOf<T>(items: readonly T[], idOf: (item: T) => string, id: string): string | null | undefined {
  const index = items.findIndex(item => idOf(item) === id)
  if (index < 0)
    return undefined
  return index > 0 ? idOf(items[index - 1]!) : null
}
